import {
  DESKTOP_VIEWPORT_FALLBACK,
  VIEWPORT_TOKENS,
  type LayoutMode,
  type PointerMode,
  type ViewportCapabilities,
} from './tokens'

const LAYOUT_QUERY_PARAM = 'layout'
const LAYOUT_STORAGE_KEY = 'f2h-layout-mode'

function readLayoutOverride(): LayoutMode | null {
  if (typeof window === 'undefined') return null

  const fromQuery = new URLSearchParams(window.location.search).get(LAYOUT_QUERY_PARAM)
  if (fromQuery === 'compact' || fromQuery === 'desktop') return fromQuery

  try {
    const stored = window.localStorage.getItem(LAYOUT_STORAGE_KEY)
    if (stored === 'compact' || stored === 'desktop') return stored
  } catch {
    // private mode / blocked storage
  }

  return null
}

function readPointerMode(): PointerMode {
  if (typeof window === 'undefined') return 'fine'
  if (window.matchMedia('(pointer: coarse)').matches) return 'coarse'
  if (window.matchMedia('(hover: none)').matches) return 'coarse'
  return 'fine'
}

function readSafeArea(): ViewportCapabilities['safeArea'] {
  if (typeof window === 'undefined') return DESKTOP_VIEWPORT_FALLBACK.safeArea

  const styles = window.getComputedStyle(document.documentElement)
  const readInset = (name: string) => {
    const raw = styles.getPropertyValue(name).trim()
    const parsed = Number.parseFloat(raw)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return {
    top: readInset('--safe-area-inset-top') || readInset('env(safe-area-inset-top)'),
    bottom: readInset('--safe-area-inset-bottom') || readInset('env(safe-area-inset-bottom)'),
    left: readInset('--safe-area-inset-left') || readInset('env(safe-area-inset-left)'),
    right: readInset('--safe-area-inset-right') || readInset('env(safe-area-inset-right)'),
  }
}

export function readViewportCapabilities(): ViewportCapabilities {
  if (typeof window === 'undefined') return DESKTOP_VIEWPORT_FALLBACK

  const width = window.innerWidth
  const height = window.innerHeight
  const override = readLayoutOverride()
  const pointerMode = readPointerMode()
  const compactByWidth = width < VIEWPORT_TOKENS.compactMaxWidthPx
  const compactByTablet =
    pointerMode === 'coarse' && width < VIEWPORT_TOKENS.tabletCompactMaxWidthPx

  const layoutMode: LayoutMode = override ?? (compactByWidth || compactByTablet ? 'compact' : 'desktop')
  const isCompactTablet =
    layoutMode === 'compact' &&
    Math.min(width, height) >= VIEWPORT_TOKENS.compactTabletMinShortSidePx

  return {
    layoutMode,
    pointerMode,
    orientation: height >= width ? 'portrait' : 'landscape',
    width,
    height,
    isCompactTablet,
    safeArea: readSafeArea(),
  }
}

/** Cotas 2D/3D: solo se agrandan en tablet; el móvil conserva el tamaño original. */
export function scaleCotaFont(size: number) {
  const { isCompactTablet } = readViewportCapabilities()
  if (isCompactTablet) return Math.round(size * 1.55)
  return size
}

/** Phone/tablet with touch. Never a mouse desktop, even if the window is narrow. */
export function isLightweightViewer(): boolean {
  if (typeof window === 'undefined') return false
  const { layoutMode, pointerMode } = readViewportCapabilities()
  return layoutMode === 'compact' && pointerMode === 'coarse'
}

/** Let Safari paint between heavy GLB parses so the tab is not killed. */
export function yieldToMainThread(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => requestAnimationFrame(() => resolve()), 0)
  })
}
